import { memo, useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { CrmSetupBillingVariant } from "../../types";
import CrmSetupContractFluxmedPackagesSection from "./CrmSetupContractFluxmedPackagesSection";
import CrmSetupContractMetaBillingSection from "./CrmSetupContractMetaBillingSection";

export interface CrmSetupStepContentCardProps {
  title: string;
  description: string;
  /** Qual painel de pagamento exibir conforme a opção salva no passo de configuração. */
  billingVariant: CrmSetupBillingVariant;
  /** Chamado quando o usuário conclui o pagamento do pacote Fluxmed. */
  onFluxmedContractActivated?: () => void;
}

const CrmSetupStepContentCard = memo(
  ({
    title,
    description,
    billingVariant,
    onFluxmedContractActivated,
  }: CrmSetupStepContentCardProps) => {
    const fallbackParagraphs = useMemo(
      () => [
        "Nenhuma opção de cobrança foi detectada. Volte ao passo de configuração e escolha entre integração Meta ou pacotes Fluxmed.",
        "Depois de salvar a opção, retorne aqui para concluir o pagamento correspondente.",
      ],
      [],
    );

    const scrollMaxHeight =
      billingVariant === "unknown"
        ? { xs: "55vh", sm: "52vh" }
        : billingVariant === "fluxmed"
          ? // Checkout Fluxmed abre em modal; aqui só a lista de pacotes.
            { xs: "65vh", sm: "58vh" }
          : // Meta: conteúdo do passo de extensão pode ser alto.
            { xs: "90vh", sm: "90vh" };

    const shouldRenderHeading = billingVariant === "unknown";

    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: { xs: 2, sm: 2.5 },
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          maxHeight: scrollMaxHeight,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pr: { xs: 0.5, sm: 1 },
          }}
        >
          {shouldRenderHeading ? (
            <>
              <Typography variant="h6" fontWeight={700}>
                {title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                {description}
              </Typography>
            </>
          ) : null}

          <Box sx={{ mt: 2 }}>
            {billingVariant === "meta" ? (
              <CrmSetupContractMetaBillingSection />
            ) : null}
            {billingVariant === "fluxmed" ? (
              <CrmSetupContractFluxmedPackagesSection
                onContractActivated={onFluxmedContractActivated}
              />
            ) : null}
            {billingVariant === "unknown" ? (
              <Stack spacing={1.5}>
                {fallbackParagraphs.map((paragraph, index) => (
                  <Typography key={index} variant="body2" color="text.secondary">
                    {paragraph}
                  </Typography>
                ))}
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  },
);

CrmSetupStepContentCard.displayName = "CrmSetupStepContentCard";

export default CrmSetupStepContentCard;
